<script setup lang="ts">
import { ref } from 'vue';
import { useDisplay } from 'vuetify';
import ConfirmDialog from './ConfirmDialog.vue';
import useLocale from './helpers/use-locale';
import DrawerBottom from './DrawerBottom.vue';

const { t } = useLocale();
const { smAndDown: isTabletOrSmaller } = useDisplay();
const confirmDialog = ref<InstanceType<typeof ConfirmDialog> | null>(null);

type Emits = {
  (e: 'close'): void;
  (e: 'share'): void;
  (e: 'unshare'): void;
  (e: 'cancel'): void;
};
const emit = defineEmits<Emits>();

const modelValue = defineModel<boolean>({ default: false });

// TODO: Refactor DialogDrawer and remove hasDoneButtonToClose, hasDoneButtonToSave and hasCancelButton and move ConfirmDialog
type Props = {
  titleText: string;
  cardColor?: string;
  closeBtnColor?: string;
  hasDoneButtonToClose?: boolean;
  hasDoneButtonToShare?: boolean;
  hasCancelButton?: boolean;
  fullHeight?: boolean;
  maxWidth?: string;
};
withDefaults(defineProps<Props>(), {
  closeBtnColor: 'primary',
  hasDoneButtonToClose: false,
  hasDoneButtonToShare: false,
  hasCancelButton: false,
  maxWidth: '70%',
});

const onSubmit = async (event: Event) => {
  event.preventDefault();
  if (
    confirmDialog.value !== null &&
    (await confirmDialog.value.open(
      'components.confirmation_text',
      '',
      'components.confirmation_cancel',
      'components.confirmation_save',
    ))
  ) {
    emit('share');
    emit('close');
  } else {
    emit('unshare');
    emit('close');
  }
};
</script>

<template>
  <DrawerBottom
    v-if="isTabletOrSmaller"
    :model-value="modelValue"
    :title="titleText"
    :color="cardColor"
    @close="emit('close')"
  >
    <template #topbar>
      <slot name="drawerTopbar">
        <slot name="topbar"></slot>
      </slot>
    </template>
    <slot></slot>
    <div v-if="$slots.actions" class="d-flex justify-end ga-4 pt-4">
      <slot name="actions"></slot>
    </div>
  </DrawerBottom>
  <v-dialog
    v-else
    :model-value="modelValue"
    :height="'100%'"
    v-bind="{ ...$attrs }"
    :max-width="maxWidth"
    persistent
    :close-on-content-click="false"
    class="dialog"
    @keydown.esc="emit('close')"
    @click:outside="onSubmit"
    @close="emit('close')"
    :aria-label="titleText"
    :aria-modal="true"
  >
    <v-card
      class="pt-4 pb-5 drawer-content rounded-lg px-8"
      :class="[fullHeight ? 'h-100vh' : 'h-auto', !cardColor ? 'bg-surface-container' : '']"
      :color="cardColor ? cardColor : ''"
    >
      <slot name="dialogTopbar">
        <slot name="topbar">
          <div class="d-flex justify-space-between align-center mb-1 mt-2">
            <v-btn
              v-if="hasCancelButton"
              :color="closeBtnColor"
              variant="text"
              rounded
              class="add-dialog-close"
              @click="emit('cancel')"
            >
              <span class="text-none">{{ t('components.cancel') }}</span>
            </v-btn>
            <div class="text-h5">
              {{ titleText }}
            </div>
            <v-btn
              v-if="hasDoneButtonToShare"
              :color="closeBtnColor"
              variant="text"
              rounded
              class="add-dialog-close"
              @click="emit('share')"
            >
              <span class="text-none">{{ t('components.button_done') }}</span>
            </v-btn>
            <v-btn
              v-if="hasDoneButtonToClose"
              :color="closeBtnColor"
              variant="text"
              rounded
              class="add-dialog-close"
              @click="emit('close')"
            >
              <span class="text-none">{{ t('components.button_done') }}</span>
            </v-btn>
          </div>
        </slot>
      </slot>

      <slot name="default"></slot>
      <v-card-actions v-if="$slots.actions" class="px-0 pt-4 pb-0 d-flex ga-4 justify-end">
        <slot name="actions"></slot>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <ConfirmDialog v-if="hasDoneButtonToShare" ref="confirmDialog" />
</template>

<style scoped lang="scss">
@use 'sass:map';
@use 'vuetify/settings' as v;

.h-100vh {
  height: 90vh !important;
}

@media #{map.get(v.$display-breakpoints, 'sm-and-down')} {
  :deep(.v-overlay__content) {
    top: auto !important;
    bottom: 0;
    height: auto !important; // added
    margin-bottom: 0 !important;
  }
}

:deep(.v-overlay__content) {
  top: auto !important;
  height: auto !important; // added
}
.drawer-content {
  overflow: hidden !important;
  max-height: calc(90vh - 64px);
}

.v-card {
  padding-bottom: max(env(safe-area-inset-bottom, 0px), 16px) !important;
}
</style>
